import {Box, Button, Grid, RiverBreakout, Text} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useEffect, useRef, useState, type MouseEventHandler} from 'react'
import Markdown, {type Components} from 'react-markdown'

import type {OS} from '@github-ui/get-os'
import {IframeVideo} from '@github-ui/swp-core/components/IframeVideo/IframeVideo'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import {InstallerActionMenu, type InstallerItem} from './../InstallerActionMenu/InstallerActionMenu'

import Styles from './Highlight.module.css'

const ITEM_MARKDOWN_COMPONENTS: Components = {
  p: ({children}) => <>{children}</>,
  strong: ({children}) => <Text>{children}</Text>,
  code: ({children}) => (
    <Text className={Styles['Highlight__code']} font="monospace" size="100">
      {children}
    </Text>
  ),
}

export interface HighlightProps {
  video: {
    src: string
    title: string
    poster: string
  }
  heading: {
    lead: string
    body: string
  }
  cta: {
    items: InstallerItem[]
  }
  items: string[]
  initialOS?: OS
  mobileHandoff?: {
    label: string
    onOpen: MouseEventHandler<HTMLButtonElement>
  }
  a11y: {
    heading: string
  }
}

export const Highlight = ({video, heading, cta, items, initialOS, mobileHandoff, a11y}: HighlightProps) => {
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true)

    if (!wrapperRef.current || !ctaRef.current) return

    const content = wrapperRef.current.querySelector(`.${Styles['Highlight__riverBreakout']} [class*='River__content']`)
    if (!content) return

    const ctaNode = ctaRef.current

    // RiverBreakout.Content drops arbitrary children, so relocate the installer
    // split-button so it always sits directly below the content body text, and
    // keep it there across re-renders.
    const moveCta = () => {
      const contentBody = content.querySelector(`[class*='River__body-text']`)
      if (!contentBody) return
      if (ctaNode.parentElement === contentBody) return
      contentBody.appendChild(ctaNode)
    }

    const ctaObserver = new MutationObserver(() => {
      moveCta()
    })

    ctaObserver.observe(content, {childList: true})
    moveCta()

    return () => {
      ctaObserver.disconnect()
    }
  }, [])

  return (
    <div ref={wrapperRef} className={clsx(Styles['Highlight'], !isMounted && Styles['Highlight--hidden'])}>
      <Box className={clsx(Styles['Highlight__gridContent'], Styles['Highlight__gridContentWide'])}>
        <Grid columnGap="none" rowGap="none" enableGutters={false}>
          <Grid.Column span={12} className={Styles['Highlight__gridColumn']}>
            <RiverBreakout variant="gridline" className={Styles['Highlight__riverBreakout']}>
              <RiverBreakout.A11yHeading as="h2">{a11y.heading}</RiverBreakout.A11yHeading>

              <RiverBreakout.Visual
                className={Styles['Highlight__visual']}
                fillMedia={false}
                rounded={false}
                hasShadow={false}
              >
                <IframeVideo
                  className={Styles['Highlight__video']}
                  videoSrc={video.src}
                  heading={video.title}
                  image={{
                    fields: {
                      file: {
                        url: video.poster,
                      },
                    },
                  }}
                />
              </RiverBreakout.Visual>

              <RiverBreakout.Content
                trailingComponentDivider
                trailingComponent={() =>
                  items.map((item, index) => (
                    // eslint-disable-next-line @eslint-react/no-array-index-key
                    <Text key={`item-${index}`} as="p" className={Styles['Highlight__contentItem']} weight="light">
                      <Markdown components={ITEM_MARKDOWN_COMPONENTS}>{item}</Markdown>
                    </Text>
                  ))
                }
              >
                <Text>
                  <b>{heading.lead}</b> {heading.body}
                </Text>
              </RiverBreakout.Content>

              <div ref={ctaRef} className={Styles['Highlight__cta']}>
                {mobileHandoff ? (
                  <Button
                    aria-haspopup="dialog"
                    className={Styles['Highlight__mobileCta']}
                    type="button"
                    variant="primary"
                    {...getAnalyticsEvent({
                      action: mobileHandoff.label,
                      tag: 'button',
                      context: 'mobile_handoff',
                      location: 'highlight',
                    })}
                    onClick={mobileHandoff.onOpen}
                  >
                    {mobileHandoff.label}
                  </Button>
                ) : (
                  <InstallerActionMenu items={cta.items} initialOS={initialOS} location="highlight" />
                )}
              </div>
            </RiverBreakout>
          </Grid.Column>
        </Grid>
      </Box>
    </div>
  )
}
